import { Location } from '@angular/common';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationExtras, NavigationStart, Router } from '@angular/router';
import { ButtonWithAction, Download, HttpRequestDetails } from '@app/core/models';
import { environment } from '@env/environment';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import {
  InAppBrowser,
  InAppBrowserEvent,
  InAppBrowserObject,
  InAppBrowserOptions,
} from '@ionic-native/in-app-browser/ngx';
import { Keyboard } from '@ionic-native/keyboard/ngx';
import { SpinnerDialog } from '@ionic-native/spinner-dialog/ngx';
import { ViewOptions, ViewService } from '@makiwin/ngx-drupal8-rest';
import { TranslateService } from '@ngx-translate/core';
import { DrupalConstants as Drupal7Constants } from 'ngx-drupal7-services';
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { I18nService } from '../../i18n/i18n.service';
import { DeviceDetectService } from '../device-detect/device-detect.service';
import { NavigationHistory } from './navigation-history';
declare const navigator: any;
declare const cordova: any;

const NOT_PUBLIC_START_URLS = [
  '/students',
  '/messages',
  // '/quality-check',
  '/bookings',
  '/account',
  '/profile',
  '/sanction',
  '/quality',
];

@Injectable({ providedIn: 'root' })
export class NavDataService {
  displayHeader = true;
  // current opened browser page from cordova plugin iab
  currentBrowser?: InAppBrowserObject;
  // application ready to render
  appReady = true;
  // side menu open close variable
  sideMenuOpen = false;
  // current page title

  // store the data here
  storage: any;
  // vcard shared component variable, closed by default
  vCardOpen = false;
  // go premium component variable, closed by default
  goPremiumOpen = false;
  // user profile info

  // http requests Details
  httpRequestDetails: HttpRequestDetails = {
    number: 0,
  };
  cartCount: any;

  /**
   * Navigation history object to manage the navigation stack.
   */
  history = new NavigationHistory(this.router, this.localize);

  readonly currentPageTitle: WritableSignal<string> = signal<string>('');

  /**
   * A writable signal that indicates whether the currency dropdown should be displayed.
   */
  readonly displayCurrency: WritableSignal<boolean> = signal(false);
  /**
   * A writable signal that indicates whether the language dropdown should be displayed.
   */
  readonly displayLanguage: WritableSignal<boolean> = signal(true);
  /**
   * A writable signal representing the current state of the left navigation button.
   *
   * This signal holds a `ButtonWithAction` object or `undefined` if no button is set.
   * Components can subscribe to this signal to reactively update the UI when the left button changes.
   */
  readonly leftButton: WritableSignal<ButtonWithAction | undefined> = signal<ButtonWithAction | undefined>(undefined);
  /**
   * A writable signal that indicates whether the IKO logo should be displayed.
   *
   * @remarks
   * This signal holds a boolean value:
   * - `true` if the IKO logo is visible,
   * - `false` if it is hidden.
   *
   * @readonly
   */
  readonly ikoLogo: WritableSignal<boolean> = signal<boolean>(false);
  /**
   * A writable signal that indicates whether the IKO logo should be displayed at the center.
   *
   * @remarks
   * This signal holds a boolean value:
   * - `true`: The IKO logo is centered.
   * - `false`: The IKO logo is not centered.
   *
   * Components can subscribe to this signal to reactively update the UI based on the logo's position.
   */
  readonly ikoLogoCenter: WritableSignal<boolean> = signal<boolean>(false);
  /**
   * A writable signal indicating whether the IKO logo should be centered on mobile devices.
   *
   * @remarks
   * This signal is typically used to control the alignment of the IKO logo in the mobile view.
   *
   * @defaultValue false
   */
  readonly ikoLogoCenterMobile: WritableSignal<boolean> = signal<boolean>(false);
  /**
   * A writable signal that holds the array of actions (buttons) to be displayed on the right side of the header.
   *
   * @remarks
   * - The value can be an array of `ButtonWithAction` objects or `undefined` if no actions are set.
   * - Use this signal to dynamically update the header actions in the UI.
   *
   * @readonly
   */
  readonly rightHeaderActions: WritableSignal<ButtonWithAction[] | undefined> = signal<ButtonWithAction[] | undefined>(
    undefined
  );

  /**
   * Emits an event whenever the navigation title has changed.
   * Components can subscribe to this Subject to react to title updates.
   *
   * @remarks
   * This Subject does not emit any payload; it only signals that a change has occurred.
   *
   * @readonly
   */
  readonly changedTitle$: Subject<void> = new Subject<void>();

  private readonly location: Location = inject(Location);
  private readonly titleService: Title = inject(Title);

  private readonly defaultPageTitlle = 'IKO - International Kiteboarding Organization';
  // navigation router history
  private timeoutResetTitle?: NodeJS.Timeout;

  constructor(
    public router: Router,
    private deviceDetectService: DeviceDetectService,
    private inAppBrowser: InAppBrowser,
    private spinnerDialog: SpinnerDialog,
    private viewService: ViewService,
    private i18nService: I18nService,
    private keyboard: Keyboard,
    private localize: LocalizeRouterService,
    private translateService: TranslateService
  ) {
    this.resetHeaderRightButton();
    this.resetHeaderLeftButton();
    this.router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart:
          this.resetHeaderRightButton();
          this.resetHeaderLeftButton();
          this.setPageTitle({ title: '', notTrackPage: true });
          break;
        case event instanceof NavigationEnd:
          this.resetTitle();
          break;
      }
    });
  }

  /**
   * Sets the page title and updates the browser's title.
   *
   * @param title - The main title for the page. If not provided, defaults to 'IKO'.
   * @param extraDataTitle - Additional title data to append to the main title.
   * @param onlyHtmlTitle - If true, only updates the HTML title without changing the currentPageTitle signal.
   * @param notTrackPage - If true, does not emit a change event for the page title.
   */
  setPageTitle({
    title,
    extraDataTitle,
    onlyHtmlTitle,
    notTrackPage,
  }: { title?: string; extraDataTitle?: string; onlyHtmlTitle?: boolean; notTrackPage?: boolean } = {}) {
    if (this.timeoutResetTitle) {
      clearTimeout(this.timeoutResetTitle);
      this.timeoutResetTitle = undefined;
    }
    if (!onlyHtmlTitle) {
      this.currentPageTitle.set(title ?? 'IKO');
    }
    this.titleService.setTitle(
      (title ? 'IKO - ' + this.translateService.instant(title) : this.defaultPageTitlle) +
        (extraDataTitle ? ' - ' + extraDataTitle : '')
    );
    if (!notTrackPage) {
      this.changedTitle$.next();
    }
  }

  /**
   * Resets the state of the header's left button.
   *
   * If `headerLogo` is true or the current logo state is active, sets the logo state accordingly
   * and removes the left button. Otherwise, sets the left button to a default "back" action.
   *
   * @param headerLogo - Optional. If true, displays the header logo instead of the left button. Defaults to false.
   */
  resetHeaderLeftButton(headerLogo = false) {
    this.ikoLogo.set(false);
    if (this.ikoLogo() || headerLogo) {
      this.ikoLogo.set(headerLogo);
      this.leftButton.set(undefined);
      return;
    }
    this.leftButton.set({
      action: () => {
        this.navigateBack();
      },
      iconClass: 'icon-angle-left',
      type: 'button',
      display: true,
    });
  }

  closeKeyboard() {
    if (this.deviceDetectService.isCordova) {
      this.keyboard.hide();
    }
  }

  resetHeaderRightButton() {
    this.rightHeaderActions.set([
      {
        action: () => {
          this.toggleSideMenu();
        },
        iconClass: 'icon-bars',
        btnClass: 'd-block d-lg-none',
        type: 'button',
        display: true,
        // this.deviceDetectService.isMobile ||
        // (this.deviceDetectService.isTablet && !this.deviceDetectService.isTabletOrientationLandscape),
      },
    ]);
  }

  /**
   * Toggle the side menu
   */
  toggleSideMenu() {
    if (document.body.clientWidth < 1024) {
      this.sideMenuOpen = !this.sideMenuOpen;
    }
  }

  /**
   * Toggle the vCard
   */
  toggleVCard() {
    this.vCardOpen = !this.vCardOpen;
    // force close the side menu
    this.sideMenuOpen = false;
  }

  replaceUrl(url: string) {
    this.location.replaceState(url);
    this.history.replaceCurrent(url);
  }

  /**
   * navigate back when user click back button
   */
  navigateBack() {
    // remove current page from navigation history
    this.history.navigateBack();
  }

  navigate(path: string, iab = false, navExtras?: NavigationExtras, notTranslateRouter = false) {
    if (!iab) {
      if (!path.startsWith('/user/') && !notTranslateRouter) {
        path = this.localize.translateRoute(path).toString();
      }
      this.router.navigate([path], navExtras);
      this.sideMenuOpen = false;
      return;
    }
    let url: string;
    if (path.indexOf('http') === -1 && path.indexOf('mailto:') === -1 && path.indexOf('tel') === -1) {
      if (path[0] !== '/') {
        path = '/' + path;
      }
      url = Drupal7Constants.backEndUrl.slice(0, -1) + path;
    } else {
      url = path;
    }
    const options: InAppBrowserOptions = {
      location: this.deviceDetectService.isIOS ? 'no' : 'yes',
      hardwareback: 'yes',
      hideurlbar: 'yes',
      zoom: 'no',
      closebuttoncaption: this.translateService.instant('Close'),
      closebuttoncolor: '#ff6600',
      toolbarcolor: '#005eb8',
      toolbarposition: 'top',
      toolbartranslucent: 'no',
      allowInlineMediaPlayback: 'yes',
    };
    this.openExternal(url, options);
  }

  navigateToCourse(course: any, schoolId: number | string, force = false) {
    /* eslint-disable @typescript-eslint/dot-notation */
    if (!this.deviceDetectService.isCordova && !force) {
      return;
    }
    // only redirect pro // allow rec: WEB-3671
    if (environment.inAppBrowsing && environment.inAppBrowsing.course && course.type === 'pro_course') {
      this.navigate(`https://www.ikointl.com/iko-kite-School-detail?nid=${schoolId}&trainingid=${course.nid}`, true);
      return;
    }
    // const data = { activity_type: 'view', entity_type: 'node', id: course.nid };
    // this.customService.activityTracking(data).subscribe();

    this.navigate(`/courses/${course.type?.replace('_course', '')}/${schoolId}/${course.nid}`);
  }

  generateLinkCourse(course: any, schoolId: number | string, courseType?: string) {
    /* eslint-disable @typescript-eslint/dot-notation */
    if (
      environment.inAppBrowsing &&
      environment.inAppBrowsing.course &&
      (!courseType || courseType === 'recreational_course')
    ) {
      return `https://www.ikointl.com/iko-kite-School-detail?nid=${schoolId}&trainingid=${course.nid}`;
    }
    // const data = { activity_type: 'view', entity_type: 'node', id: course.nid };
    // this.customService.activityTracking(data).subscribe();

    return this.localize
      .translateRoute(`/courses/${course.type?.replace('_course', '')}/${schoolId}/${course.nid}`)
      .toString();
  }

  isPublicUrl(url: string): boolean {
    url = url.toLowerCase();
    const path = url.substring(1).split('/');
    url = '/' + path[0];
    return NOT_PUBLIC_START_URLS.indexOf(url) === -1;
  }

  showModalNotification(title: string, body: string): void {
    if (!navigator.notification) {
      alert(title + '\n' + body);
      return;
    }
    navigator.notification.alert(body, null, title);
  }

  openDownload(documentId: number): Observable<void | null> {
    if (!documentId) {
      return of(null);
    }
    const options: ViewOptions = {
      filters: {
        lang: this.i18nService.language.toLowerCase(),
        mid: documentId,
      },
    };

    return this.viewService.get('/api/v1.1/downloads.json', options).pipe(
      map((data: Download[]) => {
        if (!data.length) {
          throw new Error(this.translateService.instant('Download not found or temporarily unavailable'));
        }
        this.storage = data[0];
        this.navigate('/documents/document');
        return;
      })
    );
  }

  /**
   * Clears all query parameters from the current route.
   *
   */
  clearQueryParams() {
    this.router.navigate([], {
      queryParams: {},
    });
  }

  /**
   * open a url in the external browser.
   *
   * @param link link to open in external
   * @param target target window to open the page
   */
  private openExternal(link: string, options: InAppBrowserOptions) {
    // if cordova available use inappbrowser plugin
    if (!this.deviceDetectService.isCordova) {
      window.open(link, '_blank');
      return;
    }
    // if there is already page
    if (this.currentBrowser) {
      // close it before
      this.currentBrowser.close();
      delete this.currentBrowser;
    }
    // get the initialized browser.
    this.currentBrowser = this.inAppBrowser.create(link, '_blank', options);
    this.spinnerDialog.show(undefined, undefined, true);
    // handle loading spinner and catch mailto and tel links
    // and the deeplink to the app
    this.currentBrowser.on('loadstart').subscribe({
      next: (event: InAppBrowserEvent) => {
        if (event.url.includes('tel:') || event.url.includes('mailto:')) {
          const url = event.url.replace('http://', '');
          if (this.deviceDetectService.isIOS) {
            cordova.InAppBrowser.open(url, '_system');
          } else {
            window.open(url, '_system');
            setTimeout(() => this.currentBrowser?.executeScript({ code: 'history.back();' }), 1000);
          }
          return;
        }
        if (this.checkIfInAppNavigateInBeta(event)) {
          return;
        }
      },
      error: () => {
        this.spinnerDialog.hide();
      },
    });
    this.currentBrowser.on('loadstop').subscribe({
      next: () => {
        this.spinnerDialog.hide();
      },
      error: () => {
        this.spinnerDialog.hide();
      },
    });
    this.currentBrowser.on('loaderror').subscribe({
      next: () => {
        this.spinnerDialog.hide();
      },
      error: () => {
        this.spinnerDialog.hide();
      },
    });
    this.currentBrowser.on('exit').subscribe({
      next: () => {
        this.spinnerDialog.hide();
        delete this.currentBrowser;
      },
      error: () => {
        this.spinnerDialog.hide();
      },
    });
  }

  /**
   * Resets the page title after a short delay.
   *
   * This method sets a timeout to call `setPageTitle()` after 300 milliseconds.
   * It is useful for deferring the title reset operation, for example, after navigation events.
   * The timeout reference is stored in `timeoutResetTitle` for potential future management.
   *
   * @private
   */
  private resetTitle() {
    this.timeoutResetTitle = setTimeout(() => {
      this.setPageTitle();
    }, 1000);
  }

  /**
   * Check if the inappborwser trying to navigate to the beta site.
   * if yes will close the inapp browser and navigate to the page in the app.
   * (deeplink)
   *
   * @param event {InAppBrowserEvent}
   * Event of loadstart inappbrowser
   * @returns
   */
  private checkIfInAppNavigateInBeta(event: InAppBrowserEvent): boolean {
    if (!event.url.toLowerCase().startsWith(environment.currentWebUrl)) {
      return false;
    }
    this.navigate(event.url.replace(environment.currentWebUrl, ''), false);
    this.currentBrowser?.close();
    this.spinnerDialog.hide();
    return false;
  }
}
